<template>
  <div>
    <div class="temp-selection-line" />
    <div class="story-head">
      <img
        src="@/assets/svg/text-to-speech.svg"
        class="tts"
        @click="textToSpeech(storyIntro)"
      />
      <g-title size="s" tag="h2">{{ storyTitle }}</g-title>
      <g-markdown class="story-intro" :content="storyIntro" />
    </div>
    <div class="phone-divider">
      <img src="@/assets/svg/story-phone.svg" />
    </div>
    <div class="story-parts">
      <div
        v-for="(part, index) in storyParts"
        :key="part.id"
        class="story-part"
      >
        <story-part
          :ref="'part-ref' + part.id"
          :key="part.id"
          :index="index"
          :part-data="part"
          @entering="partEntering"
          @leaving="partLeaving"
          @click="onStoryPartClick"
          @moving="partProgress"
          @animating-in="partAnimatingIn"
        >
          <img
            :key="part.id + 'tts'"
            src="@/assets/svg/text-to-speech.svg"
            class="tts-part"
            @click.stop="textToSpeech(part.Text)"
          />
        </story-part>
        <story-part-separator
          v-if="index < storyParts.length - 1"
          :ref="'sep-ref' + index"
          :key="'sep_' + index"
          :index="index"
          :animate="index >= lastVisiblePartIndex && !bottomReached"
        />
      </div>
    </div>

    <div class="all-ok">
      <p @click="setAllOkay({ label: 'alla okay' })">
        {{ storyText.AllOkayButtonLabel }}
      </p>
    </div>

    <user-limit
      ref="userLimitRef"
      :visible="bottomReached && !userLimitSet"
      :temporary-user-limit-set="temporaryUserLimitSet"
      :call-to-scroll-text="storyText.CallToScroll"
      :limit-set-feedback-text="storyText.LimitSetFeedback"
      @set-temporary-user-limit.stop="setTemporaryUserLimit"
      @set-user-limit.stop="setUserLimit"
    />
    <ecpat-limit
      ref="ecpat-limit"
      :visible="userLimitSet"
      @click="scrollToEcpatLimit"
      @continue="navigateToFeedback"
    />
    <ecpat-modal name="ecpat-limit">
      <g-markdown :content="ecpatLimitText" />
    </ecpat-modal>
  </div>
</template>

<script>
import UserLimit from './UserLimit';
import EcpatLimit from './EcpatLimit';
import StoryPart from './StoryPart';
import EcpatModal from '@/components/EcpatModal';
import StoryPartSeparator from './StoryPartSeparator';
import gql from 'graphql-tag';
import GET_STORY from '@/graphql/story/GetStory.gql';
import GET_STORY_TEXTS from '@/graphql/story/GetStoryTexts.gql';
import UPDATE_STATISTIC from '@/graphql/story/UpdateStatistic.gql';
import Vue from 'vue';

export default {
  components: {
    'story-part': StoryPart,
    'story-part-separator': StoryPartSeparator,
    'user-limit': UserLimit,
    'ecpat-limit': EcpatLimit,
    'ecpat-modal': EcpatModal
  },
  data() {
    return {
      storyText: {
        CallToScroll: '',
        LimitSetFeedback: '',
        AllOkayButtonLabel: ''
      },
      story: false,
      voice: null,
      partsStack: [],
      userSelectedPart: null,
      userSelectedPartRef: null,
      userSelectedPartIndex: -1,
      bottomReached: false,
      temporaryUserLimitSet: false,
      temporaryUserLimitIndex: false,
      lastVisiblePartIndex: -1,
      userLimitSet: false,
      routeParamId: parseInt(this.$route.params.id),
      selectionLine: 0.68 // Osynlig linje. Den part som har centrum närmast denna linje sätt som vald
    };
  },
  computed: {
    storyTitle() {
      return this.story ? this.story.Title : '';
    },
    storyIntro() {
      return this.story ? this.story.Intro : '';
    },
    storyParts() {
      return this.story ? this.story.Parts : [];
    },
    ecpatLimitText() {
      return this.story ? this.story.EcpatLimitText : '';
    }
  },
  mounted() {
    const synth = window.speechSynthesis;
    this.voice = synth
      .getVoices()
      .find(voice => voice.lang === 'sv-SE' || voice.lang === 'sv_SE');
  },
  apollo: {
    storyText: {
      query: GET_STORY_TEXTS
    },
    story: {
      query: GET_STORY,
      variables() {
        return {
          storyId: this.routeParamId
        };
      },
      notifyOnNetworkStatusChange: true,
      result: ({ data }, key) => {
        if (data && data.hasOwnProperty('story')) {
          window.sessionStorage.setItem(
            'currentStory',
            JSON.stringify({ storyId: data.story.id })
          );
        }
      }
    }
  },
  methods: {
    partEntering(event) {
      const stackIndex = this.partsStack.findIndex(
        part => part.id === event.id
      );
      if (stackIndex === -1) {
        this.partsStack.push({
          id: event.id,
          visible: event.visible,
          percentage: 0,
          pagePercentage: 0
        });
      } else {
        this.partsStack[stackIndex].visible = event.visible;
      }
    },
    partLeaving(event) {
      const stackIndex = this.partsStack.findIndex(
        part => part.id === event.id
      );
      if (stackIndex === -1) {
        this.partsStack.push({
          id: event.id,
          visible: event.visible,
          percentage: 0
        });
      } else {
        this.partsStack[stackIndex].visible = event.visible;
      }
    },
    partProgress(event) {
      const stackIndex = this.partsStack.findIndex(
        part => part.id === event.id
      );

      if (stackIndex !== -1) {
        this.partsStack[stackIndex].percentage = event.percentage;
        this.partsStack[stackIndex].pagePercentage = event.pagePercentage;

        if (stackIndex === this.partsStack.length - 1) {
          if (event.percentage <= 0.68 && !this.bottomReached) {
            this.bottomReached = true;
          }
        }
      }
    },
    partAnimatingIn(index) {
      this.lastVisiblePartIndex =
        index > this.lastVisiblePartIndex ? index : this.lastVisiblePartIndex;
    },
    onStoryPartClick(id) {
      //console.log('onStoryPartClick', id);
      if (!this.bottomReached) {
        return;
      }

      this.inactivateAllParts();
      this.temporaryUserLimitSet = true;
      this.userSelectedPartRef = this.$refs['part-ref' + id][0];
      this.temporaryUserLimitIndex = this.userSelectedPartRef.index;

      const closestPart = this.$refs['part-ref' + id][0].$el;
      this.userSelectedPartRef = this.$refs['part-ref' + id][0];
      const userLimitElementRef = this.$refs['userLimitRef'];
      const userLimitElement = this.$refs['userLimitRef'].$el;

      const rect = closestPart.getBoundingClientRect();

      const viewportHeight = window.innerHeight;

      let offset = viewportHeight - userLimitElement.offsetHeight - rect.height;

      //this.selectionLine * viewportHeight - closestPart.offsetHeight / 2;
      const elemTop = rect.top + window.scrollY;
      const delta = parseInt(Math.abs(window.scrollY - elemTop + offset));
      const duration = 150 + delta;

      const options = {
        easing: 'ease-in-out',
        offset: -offset,
        force: true,
        cancelable: false,

        onDone: () => {
          this.temporaryUserLimitSet = true;
          this.userSelectedPartRef.activateUserLimit();
          userLimitElementRef.tempLimitChanged();
        }
      };
      this.$scrollTo(closestPart, duration, options);
    },
    setTemporaryUserLimit(event) {
      return;
      //console.log('setTemporaryUserLimit');
      this.inactivateAllParts();

      const closest = this.partsStack.reduce((currentlyClosest, part) => {
        if (!part) {
          return currentlyClosest;
        } else {
          if (!part.visible) {
            return currentlyClosest;
          }

          if (
            part.percentage < this.selectionLine &&
            Math.abs(currentlyClosest.percentage - this.selectionLine) >
              Math.abs(part.percentage - this.selectionLine)
          ) {
            return part;
          } else {
            return currentlyClosest;
          }
        }
      });

      const closestPart = this.$refs['part-ref' + closest.id][0].$el;
      this.userSelectedPartRef = this.$refs['part-ref' + closest.id][0];
      const userLimitElement = this.$refs['userLimitRef'].$el;

      const rect = closestPart.getBoundingClientRect();

      const viewportHeight = window.innerHeight;

      let offset = viewportHeight - userLimitElement.offsetHeight - rect.height;

      //this.selectionLine * viewportHeight - closestPart.offsetHeight / 2;
      const elemTop = rect.top + window.scrollY;
      const delta = parseInt(Math.abs(window.scrollY - elemTop + offset));
      const duration = 150 + delta;

      const options = {
        easing: 'ease-in-out',
        offset: -offset,
        force: true,
        cancelable: false,

        onDone: () => {
          this.temporaryUserLimitSet = true;
          this.userSelectedPartRef.activateUserLimit();

          setTimeout(() => {
            window.addEventListener('scroll', this.onScroll);
          }, 150);
        }
      };
      this.$scrollTo(closestPart, duration, options);
    },

    async setUserLimit(event) {
      /*    const closest = this.partsStack.reduce((currentlyClosest, part) => {
        if (!part) {
          return currentlyClosest;
        } else {
          if (!part.visible) {
            return currentlyClosest;
          }

          if (
            part.percentage < this.selectionLine &&
            Math.abs(currentlyClosest.percentage - this.selectionLine) >
              Math.abs(part.percentage - this.selectionLine)
          ) {
            return part;
          } else {
            return currentlyClosest;
          }
        }
      });

      this.userSelectedPart = closest;
      this.userSelectedPartIndex = this.partsStack.findIndex(
        part => part.id === closest.id
      ); */

      const storyObject = {
        storyId: this.routeParamId,
        limit: this.temporaryUserLimitIndex + 1
      };

      window.sessionStorage.setItem(
        'currentStory',
        JSON.stringify(storyObject)
      );

      const userObject = JSON.parse(
        window.sessionStorage.getItem('userObject')
      );
      if (userObject) {
        if (userObject.hasOwnProperty('stories')) {
          const userStories = userObject.stories;
          const existingStory = userStories.findIndex(
            story =>
              parseInt(story.storyId, 10) === parseInt(this.routeParamId, 10)
          );

          if (existingStory !== -1) {
            userObject.stories[existingStory].limit =
              this.temporaryUserLimitIndex + 1;
          } else {
            userObject.stories.push(storyObject);
          }
        } else {
          userObject['stories'] = [storyObject];
        }

        window.sessionStorage.setItem('userObject', JSON.stringify(userObject));

        if (userObject.hasOwnProperty('statistic_id')) {
          const response = await this.$apollo.mutate({
            mutation: UPDATE_STATISTIC,
            variables: {
              input: {
                where: {
                  id: parseInt(userObject.statistic_id)
                },
                data: {
                  Data: userObject
                }
              }
            }
          });
        }
      }

      this.userLimitSet = true;
      //this.scrollToEcpatLimit();
      this.navigateToFeedback();
    },
    async setAllOkay(event) {
      const storyObject = {
        storyId: this.routeParamId,
        limit: event.label
      };

      window.sessionStorage.setItem(
        'currentStory',
        JSON.stringify(storyObject)
      );

      const userObject = JSON.parse(
        window.sessionStorage.getItem('userObject')
      );
      if (userObject) {
        if (userObject.hasOwnProperty('stories')) {
          const userStories = userObject.stories;
          const existingStory = userStories.findIndex(
            story =>
              parseInt(story.storyId, 10) === parseInt(this.routeParamId, 10)
          );

          if (existingStory !== -1) {
            userObject.stories[existingStory].limit = event.label;
          } else {
            userObject.stories.push(storyObject);
          }
        } else {
          userObject['stories'] = [storyObject];
        }

        window.sessionStorage.setItem('userObject', JSON.stringify(userObject));

        if (userObject.hasOwnProperty('statistic_id')) {
          const response = await this.$apollo.mutate({
            mutation: UPDATE_STATISTIC,
            variables: {
              input: {
                where: {
                  id: parseInt(userObject.statistic_id)
                },
                data: {
                  Data: userObject
                }
              }
            }
          });
        }
      }

      this.userLimitSet = true;
      //this.scrollToEcpatLimit();
      this.navigateToFeedback();
    },

    scrollToEcpatLimit() {
      const viewportHeight = window.innerHeight;

      if (!this.story.EcpatLimit) {
        return;
      }

      const moralSeparatorElement = this.$refs[
        'sep-ref' + this.partsStack[this.story.EcpatLimit - 1].id
      ][0].$el;
      const ecpatLimitHeight = this.$refs['ecpat-limit'].$el.offsetHeight;

      const offset = viewportHeight - ecpatLimitHeight - 90;

      var rect = moralSeparatorElement.getBoundingClientRect();
      const elemTop = rect.top - 90 + window.scrollY;
      const delta = parseInt(Math.abs(window.scrollY - elemTop + offset));
      const duration = 150 + delta;

      var options = {
        easing: 'ease-in-out',
        offset: -offset,
        force: true,
        cancelable: false,

        onDone: () => {
          this.$modal.show('ecpat-limit');
          setTimeout(() => {
            window.addEventListener('scroll', this.onScroll);
          }, 25);
        }
      };
      this.$scrollTo(moralSeparatorElement, duration, options);
    },
    onScroll(e) {
      if (this.temporaryUserLimitSet && !this.userLimitSet) {
        this.temporaryUserLimitSet = false;
      }
      window.removeEventListener('scroll', this.onScroll);
    },
    inactivateAllParts() {
      for (const prop in this.$refs) {
        if (prop.includes('part-ref')) {
          this.$refs[prop][0].inactivateUserLimit();
        }
      }
    },
    activateSelectedPart() {
      this.userSelectedPart.activateUserLimit();
    },
    navigateToFeedback() {
      this.$router.push({ name: 'feedback' });
    },
    textToSpeech(text) {
      const synth = window.speechSynthesis;

      if (synth.speaking) {
        synth.cancel();
        return;
      }
      const utterThis = new SpeechSynthesisUtterance(text);
      let voice = synth
        .getVoices()
        .find(voice => voice.lang === 'sv-SE' || voice.lang === 'sv_SE');

      if (voice) {
        utterThis.voice = voice;
        utterThis.lang = voice.lang;
        synth.speak(utterThis);
      }

      const userObject = JSON.parse(
        window.sessionStorage.getItem('userObject')
      );
      if (userObject) {
        if (userObject.hasOwnProperty('ttsCount')) {
          const ttsCount = parseInt(userObject.ttsCount) + 1;
          userObject['ttsCount'] = ttsCount;
        } else {
          userObject['ttsCount'] = 1;
        }
        window.sessionStorage.setItem('userObject', JSON.stringify(userObject));
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.phone-divider {
  text-align: center;
  padding: 50px 0;
}

.temp-selection-line {
  background-color: rgba(255, 0, 0, 0.2);
  height: 1px;
  left: 0;
  position: fixed;
  top: 32vh;
  visibility: hidden;
  width: 100%;
  z-index: 6;
}

.story-head {
  position: relative;
  padding: 36px 10px 24px 18px;

  border-radius: 30px;
  border: 2px solid #fff;

  .tts {
    cursor: pointer;
    position: absolute;
    top: -16px;
    right: -10px;
  }
}

.story-intro {
  padding: 0;
  margin-top: 20px;
  font-size: 16px;
  font-weight: 400;

  .md-body {
    margin-bottom: 0;
  }
}
.story-part {
  position: relative;

  .tts-part {
    cursor: pointer;
    position: absolute;
    top: -10px;
    right: -10px;
  }
}
.all-ok {
  padding: 50px 25px 25px;
  text-align: center;
  text-decoration: underline;

  /*  background-color: $green;
  border-radius: 20px;
  box-shadow: 0 0 9px 2px rgba(0, 0, 0, 0.4);
  color: $white;
  font-size: 16px;
  font-weight: 400;
  padding: 25px; */
}
</style>

<style lang="scss">
.story-part {
  .view-in--gt-half {
    + .tts-part {
      opacity: 1;
    }
  }
}
</style>
